import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Modal from '../Modal';
import { DownloadWithBackgroundIcon } from '../../../../icons/SvgIcons';
import { FileUpload } from '../../FileUpload/FileUpload';
import { TextButton } from '../../Button/Buttons';
import {
  BulkImportResponse,
  BulkImportTemplateFormat
} from '../../../../types/AISystems.types';
import {
  bulkImportAiSystems,
  downloadBulkImportTemplate,
  fetchAISystems
} from '../../../../services/AISystemsService';
import {
  dedupeRegulationsByCode,
  fetchRegulations
} from '../../../../services/RegulationsService';
import { RootState } from '../../../../store';
import { resetFileState } from '../../../../store/slices/fileUploadSlice';
import ModalBulkImportErrorLog from './ModalBulkImportErrorLog';
import {
  fetchSystemsFailure,
  fetchSystemsStart,
  fetchSystemsSuccess
} from '../../../../store/slices/aiSystemsSlice';
import { showToast } from '../../../../store/slices/toastSlice';
import { fetchSummaryData } from '../../../../store/slices/summarySlice';
import { useAppDispatch } from '../../../../hooks/redux';
import { buildAISystemsQuery } from '../../../../utils/AiSystems.util';

type ModalBulkImportProps = {
  showBulkModal: boolean;
  setShowBulkModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const supportedFiles = {
  fileName: ['CSV', 'XLSX'],
  acceptFile: [
    'text/csv', // csv
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' // xlsx
  ]
};

// Shown when /regulations can't be reached so the hint never renders empty.
const FALLBACK_REGULATION_CODES = ['EU_AI_ACT', 'NYC_LL144'];

const downloadTemplateItems = [
  {
    label: 'Download.xlsx',
    format: 'xlsx' as BulkImportTemplateFormat
  },
  {
    label: 'Download.csv',
    format: 'csv' as BulkImportTemplateFormat
  }
];

const ModalBulkImport = ({
  showBulkModal,
  setShowBulkModal
}: ModalBulkImportProps) => {
  const files = useSelector((state: RootState) => state.fileUpload);
  const { systems, filters, pagination, sorting } = useSelector(
    (state: RootState) => state.aiSystems
  );
  const dispatch = useAppDispatch();

  const [downloadingTemplateFormat, setDownloadingTemplateFormat] =
    useState<BulkImportTemplateFormat | null>(null);
  const [importResult, setImportResult] = useState<BulkImportResponse | null>(
    null
  );
  const [importing, setImporting] = useState(false);
  const [showErrorLogModal, setShowErrorLogModal] = useState(false);
  const [regulationCodes, setRegulationCodes] = useState<string[]>(
    FALLBACK_REGULATION_CODES
  );

  // Each row's Regulation column must be one of these codes. Fetch the live
  // list whenever the modal opens so the hint never shows a stale set; keep the
  // fallback if the request fails or returns nothing.
  useEffect(() => {
    if (!showBulkModal) return;

    let isActive = true;

    fetchRegulations()
      .then((regulations) => {
        if (!isActive) return;
        // /regulations returns one row per published pack version, so a
        // regulation with several published versions arrives several times.
        // Collapse to the latest per code. Guard AFTER deduping: it drops rows
        // without a code, so a non-empty response can still dedupe to nothing,
        // and the fallback hint beats an empty one.
        const deduped = dedupeRegulationsByCode(regulations);
        if (deduped.length === 0) return;

        setRegulationCodes(deduped.map((r) => r.regulationCode));
      })
      .catch((error) => {
        console.error('Failed to fetch regulations:', error);
      });

    return () => {
      isActive = false;
    };
  }, [showBulkModal]);

  const refreshAiSystems = async () => {
    dispatch(fetchSystemsStart());

    try {
      // Keep the caller's loaded window (offset 0 .. whatever "Load more"
      // has pulled in) while refetching under the current filters.
      const response = await fetchAISystems(
        buildAISystemsQuery(
          { filters, pagination, sorting },
          { offset: 0, limit: Math.max(pagination.limit, systems.length) }
        )
      );

      dispatch(fetchSystemsSuccess(response));
      dispatch(fetchSummaryData(filters.regulationCode || undefined));
    } catch (error) {
      dispatch(
        fetchSystemsFailure(
          error instanceof Error
            ? error.message
            : 'Failed to refresh AI systems'
        )
      );
    }
  };

  const handleDonwloadTemplate = async (format: BulkImportTemplateFormat) => {
    if (downloadingTemplateFormat) return;

    setDownloadingTemplateFormat(format);

    try {
      const blob = await downloadBulkImportTemplate(format);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `bulk-import-template.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      dispatch(
        showToast({
          message: `bulk-import-template.${format} successfully downloaded!`,
          type: 'success'
        })
      );
    } catch (error) {
      console.error('Failed to download bulk import template:', error);
    } finally {
      setDownloadingTemplateFormat(null);
    }
  };

  const handleImport = async () => {
    const selectedFile = files[0]?.file;
    if (!selectedFile || importing) return;

    setImporting(true);
    try {
      const response = await bulkImportAiSystems(selectedFile);
      await refreshAiSystems();

      if (response.skippedRows > 0 || (response.errors?.length ?? 0) > 0) {
        setImportResult(response);
        setShowErrorLogModal(true);
        setShowBulkModal(false);
        return;
      }
      dispatch(
        showToast({
          message: `Import Successful: ${response.createdRows} systems created, ${response.updatedRows} systems updated.`,
          type: 'success'
        })
      );
      dispatch(resetFileState());
      setShowBulkModal(false);
    } catch (error) {
      console.error('Failed to import AI systems:', error);
      dispatch(
        showToast({
          message:
            error instanceof Error
              ? error.message
              : 'Failed to import AI systems',
          type: 'error'
        })
      );
    } finally {
      setImporting(false);
    }
  };

  const handleDoneButton = () => {
    setShowErrorLogModal(false);
    setImportResult(null);
    dispatch(resetFileState());
  };

  return (
    <>
      <Modal open={showBulkModal} dialogPanelClassnames="w-[820px]">
        <div className="flex flex-col justify-between gap-6">
          <span className="text-lg text-gray-900 font-medium">
            Bulk Import AI Systems
          </span>
          <div className="flex justify-between p-4 bg-blue-50 rounded-lg">
            <div className="w-full flex items-center gap-3">
              <DownloadWithBackgroundIcon />
              <span className="text-purple-900 text-sm font-medium">
                Don't have the template?
              </span>
            </div>
            <div className="w-full flex items-center justify-end">
              {downloadTemplateItems.map((template) => (
                <TextButton
                  key={template.label}
                  onClick={() => handleDonwloadTemplate(template.format)}
                  disabled={downloadingTemplateFormat === template.format}
                  className="text-xs h-[30px] w-[106px]"
                >
                  {downloadingTemplateFormat === template.format
                    ? 'Downloading...'
                    : template.label}
                </TextButton>
              ))}
            </div>
          </div>
          <p className="text-gray-500 text-sm">
            Each row must include a Regulation column. Accepted regulation codes:{' '}
            <span className="text-gray-900 font-medium">
              {regulationCodes.join(', ')}
            </span>
          </p>
          <FileUpload
            handleCancel={() => {
              dispatch(resetFileState());
              setShowBulkModal(false);
            }}
            handleUpload={handleImport}
            maxFiles={1}
            minFiles={1}
            maxFileSize={15 * 1000 * 1000} // 15mb
            uploading={importing}
            buttonText={importing ? 'Importing...' : 'Import'}
            suportedFiles={supportedFiles}
            supportedFileText="CSV, XLSX only. Max 15MB. Requires a Regulation column."
          />
        </div>
      </Modal>

      <ModalBulkImportErrorLog
        importResult={importResult}
        showErrorLogModal={showErrorLogModal}
        handleDoneButton={handleDoneButton}
      />
    </>
  );
};

export default ModalBulkImport;
